/**
 * Cycle Builder Wizard – shared draft store
 * Manages state across Step 1 and Step 2 before final save.
 */
import React, { createContext, useContext, useReducer, useCallback } from "react";

export type DurationUnit = "months" | "years";

export interface WizardCriteriaItem {
  unit_no: string;
  element_no: string;
  element: string;
  criteria: string;
  type: "demonstrate" | "explain";
  status?: string;
  /** Set for rows added via "Add custom row"; excluded from template grouping; omitted on save */
  wizardSource?: "custom";
  /** Stable id for list keys (wizard-only) */
  clientKey?: string;
  /** Ties row to a global template line so it can be hidden in the library until removed (wizard-only) */
  libraryKey?: string;
  /** Template row removed from the cycle but kept in the list so it can be restored (wizard-only) */
  excluded?: boolean;
}

export interface WizardAssessmentEvent {
  event_name: string;
  description?: string;
  offset_months_open: number;
  offset_months_close: number;
  sort_order: number;
  /** Legacy/optional label; continuous renewal uses schedule order instead. */
  event_role?: "standard" | "summary" | "last_assessment";
}

export interface WizardState {
  step: 1 | 2;
  title: string;
  durationValue: number;
  durationUnit: DurationUnit;
  criteriaItems: WizardCriteriaItem[];
  /** Selected template id for company fixed-template flow (no criteria drag/edit in Step 1). */
  selectedFrameworkTemplateId?: string;
  /** When true, Cycle Info title/duration/unit are read-only (fixed template selected). */
  cycleInfoLocked?: boolean;
  useStandardLabels: boolean;
  customLabelDemonstrate?: string;
  customLabelExplain?: string;
  assessmentEvents: WizardAssessmentEvent[];
}

const initialState: WizardState = {
  step: 1,
  title: "",
  durationValue: 2,
  durationUnit: "years",
  criteriaItems: [],
  cycleInfoLocked: false,
  useStandardLabels: true,
  assessmentEvents: [],
};

type WizardAction =
  | { type: "SET_STEP"; step: 1 | 2 }
  | { type: "SET_TITLE"; title: string }
  | { type: "SET_DURATION"; value: number; unit: DurationUnit }
  | { type: "SET_CRITERIA"; items: WizardCriteriaItem[] }
  | { type: "SET_SELECTED_FRAMEWORK_TEMPLATE"; frameworkId?: string }
  | { type: "SET_CYCLE_INFO_LOCKED"; locked: boolean }
  | { type: "ADD_CRITERIA"; item: WizardCriteriaItem }
  | { type: "REMOVE_CRITERIA"; index: number }
  | { type: "SET_LABEL_MODE"; useStandard: boolean; demo?: string; explain?: string }
  | { type: "SET_EVENTS"; events: WizardAssessmentEvent[] }
  | { type: "ADD_EVENT"; event: WizardAssessmentEvent }
  | { type: "UPDATE_EVENT"; index: number; event: Partial<WizardAssessmentEvent> }
  | { type: "REMOVE_EVENT"; index: number }
  | { type: "RESET" };

function wizardReducer(state: WizardState, action: WizardAction): WizardState {
  switch (action.type) {
    case "SET_STEP":
      return { ...state, step: action.step };
    case "SET_TITLE":
      return { ...state, title: action.title };
    case "SET_DURATION":
      return { ...state, durationValue: action.value, durationUnit: action.unit };
    case "SET_CRITERIA":
      return { ...state, criteriaItems: action.items };
    case "SET_SELECTED_FRAMEWORK_TEMPLATE":
      return { ...state, selectedFrameworkTemplateId: action.frameworkId?.trim() || undefined };
    case "SET_CYCLE_INFO_LOCKED":
      return { ...state, cycleInfoLocked: action.locked };
    case "ADD_CRITERIA":
      return { ...state, criteriaItems: [...state.criteriaItems, action.item] };
    case "REMOVE_CRITERIA":
      return { ...state, criteriaItems: state.criteriaItems.filter((_, i) => i !== action.index) };
    case "SET_LABEL_MODE":
      if (action.useStandard) {
        return {
          ...state,
          useStandardLabels: true,
          customLabelDemonstrate: undefined,
          customLabelExplain: undefined,
        };
      }
      return {
        ...state,
        useStandardLabels: false,
        customLabelDemonstrate:
          action.demo !== undefined ? action.demo : state.customLabelDemonstrate,
        customLabelExplain:
          action.explain !== undefined ? action.explain : state.customLabelExplain,
      };
    case "SET_EVENTS":
      return { ...state, assessmentEvents: action.events };
    case "ADD_EVENT":
      return { ...state, assessmentEvents: [...state.assessmentEvents, action.event] };
    case "UPDATE_EVENT":
      return {
        ...state,
        assessmentEvents: state.assessmentEvents.map((e, i) =>
          i === action.index ? { ...e, ...action.event } : e
        ),
      };
    case "REMOVE_EVENT":
      return { ...state, assessmentEvents: state.assessmentEvents.filter((_, i) => i !== action.index) };
    case "RESET":
      return { ...initialState };
    default:
      return state;
  }
}

export function getDurationInMonths(state: WizardState): number {
  if (state.durationUnit === "months") return state.durationValue;
  return state.durationValue * 12;
}

const WizardContext = createContext<{
  state: WizardState;
  dispatch: React.Dispatch<WizardAction>;
  durationInMonths: number;
} | null>(null);

export function CycleWizardProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(wizardReducer, initialState);
  const durationInMonths = getDurationInMonths(state);
  return (
    <WizardContext.Provider value={{ state, dispatch, durationInMonths }}>
      {children}
    </WizardContext.Provider>
  );
}

export function useCycleWizard() {
  const ctx = useContext(WizardContext);
  if (!ctx) throw new Error("useCycleWizard must be used within CycleWizardProvider");
  return ctx;
}
